import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  Scrolling,
  Selection,
  Paging,
  SearchPanel,
  Toolbar,
  Item,
  LoadPanel,
} from "devextreme-react/data-grid";
import { ReactComponent as ExportHistoryIcon } from "../../../cui/assets/img/icon/export-history.svg";
import { dataGridConfig } from "../../../constants";
import {
  taxPortalClientApi,
  createSitesClientApi,
} from "../../../api-client-nswag/taxportal-client-runtime";
import { isEmpty, isNull, set } from "lodash";
import { CheckBox } from "devextreme-react/check-box";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MDMLegalEntityDTO,
  MDMSelectedLegalEntityDTO,
} from "../../../api-client-nswag/taxportal-client";
import { OidcIdentityContext } from "@rsmus/react-auth";
import CloseIcon from "@mui/icons-material/Close";
import { useRecoilState, useRecoilValue } from "recoil";
import { getAppName } from "../../../cui/commonComponents/AppNameToggle";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { calculateGridHeight } from "../../../util/Utils";

type DatagridRefType = DataGrid;

const SelectEditLegalEntities = (props: any) => {
  const { showSnackbar } = useSnackbar();
  const { showSuccessSnackbar } = useSnackbar();
  const allowedApps = ["TP", "CS"];
  const allowedStatusForExportHistory = ["ACTIVE"];

  const [selectedKeys, setSelectedKeys] = useState<any[]>([]);
  const [unSelectedKeys, setunSelectedKeys] = useState<any[]>([]);
  const [selectedData, setSelectedData] = useState<MDMSelectedLegalEntityDTO[]>(
    [],
  );
  const [popup, setPopup] = useState(false);
  const [continueDisable, setContinueDisable] = useState(true);
  const { user } = useContext(OidcIdentityContext);
  const [syncClicked, setSyncClicked] = useState(false);
  const dataGridRef = useRef<DatagridRefType>(null);
  const [loaded, setLoaded] = useState(false);
  const appName = useRecoilValue(getAppName);
  const [taxYear, setTaxYear] = useState<number>();
  const [isError, setIsError] = useState<string | undefined>("");
  const [isConsented, setIsConsented] = useState<boolean>(true);

  useEffect(() => {
    if (
      (props.selectedLegalEntityData &&
        props.selectedLegalEntityData.length > 0) ||
      (props.removedLegalEntityData && props.removedLegalEntityData.length > 0)
    ) {
      const addedData = props.selectedLegalEntityData.map((item: any) => ({
        mdmClientId: props.selectedRowsData[0]?.mdmClientId,
        clientId: item.clientId,
        clientName: item.clientName,
        hqClientName: props.selectedRowsData[0]?.name,
        hqSiteId: props.selectedRowsData[0].hqSiteId,
        suppressFolderCreation: false,
        suppressDropdown: false,
        suppressProjectStatus: false,
        userName: user?.profile.preferred_username as string,
        isRemoved: false,
      }));

      const removedData = props.removedLegalEntityData.map((item: any) => ({
        mdmClientId: props.selectedRowsData[0]?.mdmClientId,
        clientId: item.clientId,
        clientName: item.clientName,
        hqClientName: props.selectedRowsData[0]?.name,
        hqSiteId: props.selectedRowsData[0].hqSiteId,
        suppressFolderCreation: false,
        suppressDropdown: false,
        suppressProjectStatus: false,
        userName: user?.profile.preferred_username as string,
        isRemoved: true,
      }));

      const mappedData = [...addedData, ...removedData];
      props.setDataPermission(mappedData);
    }
  }, [
    props.selectedLegalEntityData,
    props.removedLegalEntityData,
    props.selectionMode,
  ]);

  useEffect(() => {
    let timeoutId: any = null;
    let isCancelled = false;

    const fetchData = async () => {
      try {
        props.setRemovedLegalEntityData([]);
        props.setSelectedLegalEntityData([]);
        props.setLegalData({
          pending: true,
          fulfilled: false,
          gridLegalData: [],
        });
        const { mdmClientId, hqSiteId } = props.selectedRowsData[0] || {};
        const [legalEntitiesResponse, selectedEntitiesResponse] = await Promise.all([
          createSitesClientApi.mDM_GetMDMLegalEntitiesPerSite(mdmClientId, hqSiteId),
          createSitesClientApi.mDM_GetSelectedLegalEntities(hqSiteId, mdmClientId),
        ]);
        if (legalEntitiesResponse) {
          props.setLegalData((prev: any) => ({
            ...prev,
            gridLegalData: legalEntitiesResponse?.legalEntites ?? [],
          }));
          props.setSelectedLegalEntityData([]);
          const anyObfuscated =
            Array.isArray(legalEntitiesResponse.legalEntites) &&
            legalEntitiesResponse.legalEntites.some((le: any) => {
              const name = (le.clientName || le.name || "")
                .toString()
                .toLowerCase();
              return name === "obfuscated";
            });
          if (anyObfuscated) {
            setIsConsented(false);
          }
        }
        const uniqueData = selectedEntitiesResponse.entities.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.clientId === item.clientId),
        );

        if (legalEntitiesResponse?.isAllEntitiesSelected === true) {
          const uniqueData =
            legalEntitiesResponse &&
              Array.isArray(legalEntitiesResponse.legalEntites)
              ? legalEntitiesResponse.legalEntites.map((row: any) => row)
              : [];
          setSelectedData(uniqueData);
          const allKeys =
            legalEntitiesResponse &&
              Array.isArray(legalEntitiesResponse.legalEntites)
              ? legalEntitiesResponse.legalEntites
              : [];
          setSelectedKeys(allKeys);
          props.setIsAllSiteSelected(true);
          props.setSelectionMode("none");
        } else {
          props.setSelectionMode("multiple");
          setSelectedData(uniqueData);
          props.setIsAllSiteSelected(false);
        }
        props.setLegalData((prev: any) => ({
          ...prev,
          pending: false,
          fulfilled: true,
        }));
      } catch (error) {
        props.setLegalData((prev: any) => ({
          ...prev,
          pending: false,
          fulfilled: true,
        }));
        setIsError(error as string);
      } finally {
        timeoutId = setTimeout(() => {
          if (!isCancelled) {
            setLoaded(true);
          }
        }, 200);
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const onSelectionchanged = (e: any) => {
    if (props.handleEdit && props.isAllSiteSelected) {
      const allKeys = props.legalData.gridLegalData.map((row: any) => row);
      setSelectedKeys(allKeys);
      props.setSelectedLegalEntityData(allKeys);
      return;
    }
    if (!props.handleEdit) {
      const allowedDeselectedKeys = e.currentDeselectedRowKeys.filter(
        (key: any) => key.isRemovedFromMdm === true,
      );

      const filteredSelectedKeys = selectedKeys.filter(
        (key: any) =>
          !allowedDeselectedKeys.some(
            (deselected: any) => deselected.clientId === key.clientId,
          ),
      );

      if (allowedDeselectedKeys.length > 0) {
        setPopup(true);
      } else {
        setPopup(false);
      }

      const prevRemoved = props.removedLegalEntityData || [];
      let mergedRemoved = [...prevRemoved, ...allowedDeselectedKeys].filter(
        (item, index, arr) =>
          arr.findIndex((x) => x.clientId === item.clientId) === index,
      );

      mergedRemoved = mergedRemoved.filter(
        (item) =>
          !filteredSelectedKeys.some(
            (selected) => selected.clientId === item.clientId,
          ),
      );

      props.setSelectedLegalEntityData(filteredSelectedKeys);
      props.setRemovedLegalEntityData(mergedRemoved);
      setSelectedKeys(filteredSelectedKeys);
      setunSelectedKeys(mergedRemoved);
      setContinueDisable(false);
    } else {
      if (
        e.currentDeselectedRowKeys.length > 0 &&
        props.selectionMode === "multiple"
      ) {
        setPopup(true);
      } else {
        setPopup(false);
      }
      const prevRemoved = props.removedLegalEntityData || [];
      let mergedRemoved = [
        ...prevRemoved,
        ...e.currentDeselectedRowKeys,
      ].filter(
        (item, index, arr) =>
          arr.findIndex((x) => x.clientId === item.clientId) === index,
      );
      mergedRemoved = mergedRemoved.filter(
        (item) =>
          !e.selectedRowKeys.some(
            (selected: any) => selected.clientId === item.clientId,
          ),
      );

      props.setSelectedLegalEntityData(e.selectedRowKeys);
      props.setRemovedLegalEntityData(mergedRemoved);
      setSelectedKeys(e.selectedRowKeys);
      setunSelectedKeys(mergedRemoved);
      setContinueDisable(false);
    }
  };

  const onRowPrepared = (e: any) => {
    if (e.data && e.rowElement) {
      setTimeout(() => {
        const checkboxCell = e.rowElement.querySelector(".dx-select-checkbox");
        const checkboxInput = e.rowElement.querySelector(
          'input[type="checkbox"]',
        );

        if (checkboxCell || checkboxInput) {
          if (props.handleEdit) {
            if (props.isAllSiteSelected) {
              if (checkboxCell) {
                checkboxCell.style.pointerEvents = "none";
                checkboxCell.style.opacity = "0.5";
              }
              if (checkboxInput) {
                checkboxInput.disabled = true;
                checkboxInput.style.opacity = "0.5";
              }
            } else {
              if (checkboxCell) {
                checkboxCell.style.pointerEvents = "auto";
                checkboxCell.style.opacity = "1";
              }
              if (checkboxInput) {
                checkboxInput.disabled = false;
                checkboxInput.style.opacity = "1";
              }
            }
          } else {
            if (e.data.isRemovedFromMdm === true) {
              if (checkboxCell) {
                checkboxCell.style.pointerEvents = "auto";
                checkboxCell.style.opacity = "1";
              }
              if (checkboxInput) {
                checkboxInput.disabled = false;
                checkboxInput.style.opacity = "1";
              }
            } else {
              if (checkboxCell) {
                checkboxCell.style.pointerEvents = "none";
                checkboxCell.style.opacity = "0.5";
              }
              if (checkboxInput) {
                checkboxInput.disabled = true;
                checkboxInput.style.opacity = "0.5";
              }
            }
          }
        }
      }, 0);
    }
  };

  const onCellClick = (e: any) => {
    if (!props.handleEdit && e.column && e.column.type === "selection") {
      if (e.data && e.data.isRemovedFromMdm !== true) {
        e.event.preventDefault();
        e.event.stopPropagation();
        return false;
      }
    }
  };

  const onRowClick = (e: any) => {
    if (!props.handleEdit && e.data && e.data.isRemovedFromMdm !== true) {
      return;
    }
  };

  const handleClose = () => {
    setSelectedKeys((prevData) => [...prevData, ...unSelectedKeys]);
    setPopup(false);
  };

  const handleYes = () => {
    setPopup(false);
  };

  const text =
    "You are removing entities that are associated with the site. This will lead to deletion of associated Project Status records with this entity, removal of corresponding Entity, Project, Phase choices options, and hiding related folders from non-admin roles. If there existing IR record for selected entities - no actions will be made for the IR iSheet.\nAre you sure you want to continue? ";

  useEffect(() => {
    continueDisable === true
      ? props.setBtnContinueDisable(true)
      : props.setBtnContinueDisable(false);
  }, [props.selectionMode, continueDisable]);

  const refreshButtonOptions = {
    icon: "refresh",
    onClick: async () => {
      try {
        setIsError("");
        props.setLegalData((prev: any) => ({
          ...prev,
          pending: true,
          fulfilled: false,
        }));

        localStorage.setItem("Legalentities", "");

        const data = await createSitesClientApi.mDM_GetMDMLegalEntitiesPerSite(
          props.selectedRowsData?.[0]?.mdmClientId,
          props.selectedRowsData?.[0]?.hqSiteId,
        );

        const legalEntities = Array.isArray(data?.legalEntites)
          ? data.legalEntites
          : [];
        props.setLegalData({
          pending: false,
          fulfilled: true,
          gridLegalData: legalEntities,
        });
      } catch (err) {
        props.setLegalData((prev: any) => ({
          ...prev,
          pending: false,
          fulfilled: true,
        }));
        setIsError("Failed to refresh legal entities. Please try again.");
      }
    },
  };

  useEffect(() => {
    if (isConsented == false || selectedKeys.length === 0) {
      props.setBtnContinueDisable(true);
    }
    if (isConsented && selectedKeys.length > 0) {
      props.setBtnContinueDisable(false);
    }
  }, [selectedKeys]);

  useEffect(() => {
    const selectedRowKeys = props.legalData.gridLegalData
      .filter((row: { clientId: any }) =>
        selectedData.some(
          (selectedRow) => selectedRow.clientId === row.clientId,
        ),
      )
      .map((row: { clientId: any }) => row);
    setSelectedKeys(selectedRowKeys);
  }, [props.legalData.gridLegalData, selectedData]);

  useEffect(() => {
    if (dataGridRef.current) {
      dataGridRef.current.instance.repaint();
    }
  }, [props.handleEdit, props.isAllSiteSelected]);

  useEffect(() => {
    const getTaxYear = async () => {
      const taxYear = await createSitesClientApi.highQ_GetConsentedTaxYear(
        props.selectedRowsData[0]?.hqSiteId,
      );
      setTaxYear(taxYear);
    };
    getTaxYear();
  }, [props.selectedRowsData]);

  const exportButtonOptions = {
    icon: "exportxlsx",
    hint: "Export to Excel",
    onClick: async () => {
      try {
        const { mdmClientId, hqSiteId } = props.selectedRowsData[0] || {};
        var response = await taxPortalClientApi
          .mDM_ExportToExcelForLegalEntities(hqSiteId, mdmClientId, appName)
          .then((res: any) => {
            const url = URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${appName}-Legal Entities(${hqSiteId}).xlsx`,
            );
            link.click();
          });
        showSuccessSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS,
        });
      } catch (error) {
        showSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL,
          severity: "error",
        });
      }
    },
    elementAttr: {
      title: "Export to Excel",
    },
  };

  const handleExportHistory = async () => {
    try {
      const { hqSiteId } = props.selectedRowsData[0] || {};
      var response = await createSitesClientApi
        .mDM_ExportToExcelForLegalEntitiesHistory(hqSiteId, appName)
        .then((res: any) => {
          const url = URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `LE Report(${hqSiteId}).xlsx`
          );
          link.click();
        });
      showSuccessSnackbar({
        message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS,
      });
    } catch (error) {
      showSnackbar({
        message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL,
        severity: "error",
      });
    }
  }

  const cellRenderForConsent = (celldata: any) => {
    let consentGiven = false;
    if (celldata.data.consentReceivedYears !== null) {
      const str = celldata.data.consentReceivedYears;
      const arr: number[] = JSON.parse(str);
      if (taxYear != null) consentGiven = arr.includes(taxYear);
    }
    return <div>{consentGiven ? "Y" : "N"}</div>;
  };

  const cellRenderForIsRemovedFromMdm = (cellData: any) => {
    return <div>{cellData?.data?.isRemovedFromMdm ? "Y" : null}</div>;
  };

  return (
    <>
      <div>
        <div
          style={{
            margin: "0 0 10px 0",
            display: "flex",
            justifyContent: "space-between",
          }}
        ></div>
        <Dialog
          open={popup}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <div style={{ marginTop: "30px" }}>
            <DialogContent>
              <Typography style={{ whiteSpace: "pre-line" }}>{text}</Typography>
            </DialogContent>
          </div>
          <DialogActions>
            <Button
              onClick={handleClose}
              style={{ margin: "0 16px 0 0" }}
              variant="outlined"
            >
              No
            </Button>
            <Button
              onClick={handleYes}
              style={{ margin: "0 16px 0 0" }}
              variant="contained"
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <DataGrid
          ref={dataGridRef}
          {...dataGridConfig}
          showBorders={true}
          height={360}
          selectedRowKeys={selectedKeys}
          onSelectionChanged={onSelectionchanged}
          onRowPrepared={onRowPrepared}
          onCellClick={onCellClick}
          onRowClick={onRowClick}
          dataSource={props?.legalData?.gridLegalData}
          noDataText={
            props?.legalData?.pending
              ? "Loading..."
              : syncClicked
                ? "Refresh after sometime"
                : "Sync Legal Entity"
          }
          onEditorPreparing={(e) => {
            if (e.parentType === "searchPanel") {
              e.editorOptions.onValueChanged = (args: any) => {
                const searchValue = args.value?.toLowerCase() || "";
                const grid = e.component;
                grid.filter([
                  ["clientId", "contains", searchValue],
                  "or",
                  ["clientName", "contains", searchValue],
                ]);
              };
            }
          }}
        >
          <Selection mode={"multiple"} allowSelectAll={false} />
          <Scrolling mode="infinite" columnRenderingMode="virtual" />
          <Paging defaultPageSize={20} />
          <SearchPanel placeholder={"Global Search..."} visible />
          <Toolbar>
            <Item location={"before"}>{props.selectedRowsData?.name}</Item>
            <Item location="before" name="alignRight">
              <div>
                <CheckBox
                  value={props.selectionMode === "multiple" ? false : true}
                  onValueChanged={(a: any) => {
                    props.setSelectionMode(a.value ? "none" : "multiple");
                    if (a.event) {
                      setContinueDisable(false);
                      if (a.value) {
                        const allKeys = props?.legalData?.gridLegalData?.map(
                          (row: any) => row,
                        );
                        setSelectedKeys(allKeys);
                        props.setSelectedLegalEntityData(allKeys);
                        props.setIsAllSiteSelected(true);
                      } else {
                        if (props.isAllSiteSelected === true) {
                          const allKeys = props?.legalData?.gridLegalData?.map(
                            (row: any) => row,
                          );
                          setSelectedKeys(allKeys);
                          props.setSelectedLegalEntityData(allKeys);
                        } else {
                          setSelectedKeys([]);
                          props.setSelectedLegalEntityData([]);
                        }
                        props.setIsAllSiteSelected(false);
                      }
                    }
                  }}
                  disabled={!props.handleEdit}
                />
                <Tooltip
                  title="New entities are automatically added to the site when they are associated with this master client."
                  placement="top"
                >
                  <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                    Always include all legal entities
                  </span>
                </Tooltip>
                <Button
                  style={{ margin: "0 12px 0 12px " }}
                  onClick={async () => {
                    await createSitesClientApi.mDM_SyncLegalEntitiesConsent(
                      props.selectedRowsData[0]?.mdmClientId,
                      null,
                      props.selectedRowsData[0]?.hqSiteId,
                    );
                    setSyncClicked(true);
                  }}
                  variant="outlined"
                >
                  Sync Legal Entites
                </Button>
              </div>
            </Item>
            <Item name="searchPanel" location="before" />
            {allowedStatusForExportHistory.includes(props.selectedRowsData[0]?.status) && (
              <Item
                location="after"
                render={() => (
                  <div
                    title="Export Legal Entity History to Excel"
                    onClick={handleExportHistory}
                    style={{
                      padding: "5px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <ExportHistoryIcon
                      width={20}
                      height={18}
                      style={{ color: "#333" }}
                    />
                  </div>
                )}
              />
            )}
            {allowedApps.includes(appName) && !props.handleEdit && (
              <Item
                widget="dxButton"
                location="after"
                options={exportButtonOptions}
              />
            )}
            <Item
              widget="dxButton"
              options={refreshButtonOptions}
              name="alignRight"
            />
          </Toolbar>
          <Column alignment="left" dataField="clientId" caption={"Client Id"} />
          <Column
            alignment="left"
            dataField="clientName"
            caption={"Client Name"}
          />
          <Column
            alignment="left"
            dataField="consentReceivedYears"
            cellRender={cellRenderForConsent}
            caption={"Consent"}
          />
          <Column
            alignment="left"
            dataField="isRemovedFromMdm"
            caption={"Disassociated in MDM"}
            cellRender={cellRenderForIsRemovedFromMdm}
          />
        </DataGrid>
        {selectedData?.length > 0 &&
          selectedKeys?.length === 0 &&
          props?.selectionMode === "multiple" &&
          !props?.legalData?.pending &&
          loaded ? (
          <div style={{ color: "red", fontSize: "14px" }}>
            At least one entity has to be selected
          </div>
        ) : null}
        {isError !== "" && typeof isError === "string" ? (
          <div style={{ color: "red", fontSize: "14px" }}>{isError}</div>
        ) : null}
      </div>
    </>
  );
};

export default SelectEditLegalEntities;
